import cx from 'clsx';
import { ZenModeTooltip } from 'components/ZenModeTooltip';
import { StreamControlButton } from 'features/streaming/components/StreamControlButton';
import { useAppSelector } from 'store/hooks';
import { selectIsActiveSpeaker } from 'features/streaming/streamingSlice';
import { UserId } from 'features/users/types';
import { useTranslation } from 'react-i18next';
import { useRemoteAudioMute } from 'hooks/useRemoteAudioMute';

export type MicControlRemoteProps = {
  userId: UserId;
};

export const MicControlRemote = ({ userId }: MicControlRemoteProps) => {
  const { t } = useTranslation('room');

  const activeSpeaker = useAppSelector((state) => selectIsActiveSpeaker(state, userId));
  const {
    isAudioBroadcasting,
    toggleMicRemotely,
    canRemotelyMute,
    canAskToUnmute,
    isAskedToUnmuteAudio,
  } = useRemoteAudioMute(userId);

  const color = () => {
    if (activeSpeaker && isAudioBroadcasting) {
      return '#00ff00';
    }

    return 'common.white';
  };

  const renderTooltipText = () => {
    if (isAskedToUnmuteAudio) {
      return t('tooltips.asked_to_unmute_remote_audio');
    }

    if (isAudioBroadcasting) {
      return t('tooltips.mute_remote_audio');
    }

    return t('tooltips.unmute_remote_audio');
  };

  const renderMicIcon = () => {
    if (isAskedToUnmuteAudio) {
      return 'mic-clock-pending';
    }

    return isAudioBroadcasting ? 'mic' : 'mute';
  };

  const canInteract = canRemotelyMute || (canAskToUnmute && !isAskedToUnmuteAudio);

  const handleClick = () => {
    if (isAskedToUnmuteAudio) {
      return;
    }
    toggleMicRemotely();
  };

  return (
    <ZenModeTooltip title={renderTooltipText()} placement="top">
      <StreamControlButton
        icon={renderMicIcon()}
        className={cx({ 'hover-child-eager': !(activeSpeaker && isAudioBroadcasting) })}
        sx={{
          color,
        }}
        tabIndex={canInteract ? 0 : -1}
        disableInteraction={!canInteract}
        onClick={handleClick}
      />
    </ZenModeTooltip>
  );
};
